import { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../utils/cn';

/** Spacing options in units of 0.25rem (4px) */
type Space = 0 | 0.5 | 1 | 1.5 | 2 | 2.5 | 3 | 3.5 | 4 | 5 | 6 | 8 | 10 | 12 | 14 | 16 | 20;

/** Vertical alignment options for flex container items */
type Alignment = 'start' | 'center' | 'end' | 'stretch';

/** Horizontal distribution options for flex container items */
type Justify = 'start' | 'end' | 'center' | 'between' | 'around';

/** Flex wrapping behavior options for container items */
type Wrap = 'nowrap' | 'wrap' | 'wrap-reverse';

const alignmentClasses: Record<Alignment, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
};

const justifyClasses: Record<Justify, string> = {
  start: 'justify-start',
  end: 'justify-end',
  center: 'justify-center',
  between: 'justify-between',
  around: 'justify-around',
};

const wrapClasses: Record<Wrap, string> = {
  nowrap: 'flex-nowrap',
  wrap: 'flex-wrap',
  'wrap-reverse': 'flex-wrap-reverse',
};

const spaceClasses: Record<Space, `gap-x-${Space}`> = {
  0: 'gap-x-0',
  0.5: 'gap-x-0.5',
  1: 'gap-x-1',
  1.5: 'gap-x-1.5',
  2: 'gap-x-2',
  2.5: 'gap-x-2.5',
  3: 'gap-x-3',
  3.5: 'gap-x-3.5',
  4: 'gap-x-4',
  5: 'gap-x-5',
  6: 'gap-x-6',
  8: 'gap-x-8',
  10: 'gap-x-10',
  12: 'gap-x-12',
  14: 'gap-x-14',
  16: 'gap-x-16',
  20: 'gap-x-20',
};

type InlineProps = HTMLAttributes<HTMLElement | SVGElement> & {
  children: ReactNode;
  className?: string;
  space?: Space;
  align?: Alignment;
  justify?: Justify;
  wrap?: Wrap;
  as?: keyof JSX.IntrinsicElements;
};

/**
 * A flexible layout component that arranges its children in a horizontal row
 * with configurable spacing, alignment, and wrapping behavior.
 *
 * @param props.children - The content to be arranged horizontally
 * @param props.className - Optional additional CSS classes
 * @param props.space - The gap between children (0-16) (default: 2)
 * @param props.align - Vertical alignment of children (default: 'center')
 * @param props.justify - Horizontal distribution of children (default: 'start')
 * @param props.wrap - Controls how children wrap when they exceed container width (default: 'wrap')
 * @param props.as - The HTML element to render as (default: 'div')
 */
export const Inline = ({
  children,
  className,
  space = 2,
  align = 'center',
  justify = 'start',
  wrap = 'wrap',
  as: Component = 'div',
  ...props
}: InlineProps) => {
  return (
    <Component
      className={cn(
        'flex flex-row',
        spaceClasses[space],
        alignmentClasses[align],
        justifyClasses[justify],
        wrapClasses[wrap],
        className,
      )}
      {...props}
    >
      {children}
    </Component>
  );
};
